import {
  Breadcrumb,
  Button,
  Cards,
  ComponentContainer,
  CustomSpacing,
  Flex,
  Icon,
  Input,
  Section,
  Text,
  theme,
} from '@genially/public-web-components';
import { FC, useEffect, useState } from 'react';

import type { BackgroundDTO } from 'core/infrastructure/parseBackground';
import Fuse from 'fuse.js';
import { parseBackground } from 'core/infrastructure/parseBackground';
import { parseMedia } from 'core/infrastructure/parseMedia';
import { snowplowWrapper } from 'core/services/snowplow';
import { translate } from 'core/infrastructure/translator';

export interface ParagraphCardsGridItemDTO {
  entity: any;
}

export interface ParagraphSearchProps {
  behaviorSettings: {
    rowStyle: { background: BackgroundDTO; spacing: CustomSpacing };
  };
  elementsPerRow?: number;
  pagelangcode: {
    value: string;
  };
  //snowplowEvent?: () => void;
  buttonStyle?: {
    size: string;
    variant: 'academy' | 'danger' | 'primary' | 'secondary' | 'stroke' | 'tertiary' | 'strokeWhite';
  };
}

export const ParagraphSearch: FC<ParagraphSearchProps> = ({
  behaviorSettings,
  elementsPerRow = 4,
  pagelangcode,
  //snowplowEvent,
  buttonStyle,
}) => {
  type SearchEntry = {
    title: string;
    description: string;
    tags: string[];
  };

  const [fuse, setFuse] = useState<Fuse<SearchEntry> | null>(null);
  const [results, setResults] = useState<SearchEntry[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [localSearch, setLocalSearch] = useState<string>('');
  const [upperResultsText, setDisplayUpperResultsText] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const translateFromContent = (key: string) =>
    translate(pagelangcode ? pagelangcode.value : 'en', key);
  const resultText = translateFromContent('template.resulttext');
  const placeholder = translateFromContent('template.searchplaceholder');
  const [fullBreadcrumb, setBreadcrumb] = useState([] as any[]);

  useEffect(() => {
    if (!pagelangcode.value) return;
    const loadIndex = async () => {
      try {
        const res = await fetch(`/search-index-${pagelangcode.value}.json`);
        const data: SearchEntry[] = await res.json();

        const fuseInstance = new Fuse<SearchEntry>(data, {
          keys: [
            {
              name: 'title',
              weight: 0.5,
            },
            {
              name: 'description',
              weight: 0.1,
            },
            {
              name: 'tags',
              weight: 0.4,
            },
          ],
          includeScore: true,
          distance: 50,
          minMatchCharLength: 4,
          threshold: 0.2, // menor = más estricto
        });
        setFuse(fuseInstance ? fuseInstance : null);
      } catch (err) {
        console.error('❌ Error al cargar el índice:', err);
      }
    };

    loadIndex();
  }, [pagelangcode.value]);

  // Calculamos query param de la url
  useEffect(() => {
    const slug = window.location.search.split('search=').pop();
    setSlug(slug ? decodeURIComponent(slug ? slug : '') : '');
    setInputValue(slug ? decodeURIComponent(slug ? slug : '') : '');
    setLocalSearch(slug ? decodeURIComponent(slug ? slug : '') : '');
  }, []);

  useEffect(() => {
    const finalValue = localSearch ? localSearch : slug ? slug : '';
    if (fuse) {
      const found = fuse.search(finalValue).map((r: any) => ('item' in r ? r.item : r));
      if (found.length > 0) {
        setResults(found);
      } else {
        setResults([]);
        setTemplates([]);
      }
    } else {
      setResults([]);
    }
  }, [fuse, localSearch, slug]);

  useEffect(() => {
    if (!localSearch) return;
    history.replaceState(null, '', '?search=' + localSearch);
    if (!process.env.GATSBY_IS_LOCAL || process.env.GATSBY_IS_LOCAL === 'false') {
      const sendPageView = () => {
        // Snowplow tracking for all pages and all cases
        snowplowWrapper.initTracker();
        snowplowWrapper.sendPageViewEvent();
      };
      // Minimum delay for reactHelmet's requestAnimationFrame
      setTimeout(sendPageView, 32);
    }
  }, [localSearch]);

  useEffect(() => {
    if (results.length === 0) {
      setTemplates([]);
    }
    function teaserImage(item: any) {
      return {
        entity: {
          type: 'MediaImage',
          mediaImage: {
            alt: item.title ? item.title : '',
            title: item.title ? item.title : '',
            width: 1200,
            height: 675,
            src: item.thumbnail,
          },
        },
      };
    }
    const parsedTemplates: any[] = [];
    results?.map((item: any) => {
      const parsed = {
        media: parseMedia(
          teaserImage(item),
          true,
          pagelangcode.value,
          item.title ? item.title : ''
        ),
        title: {
          text: item.title,
        },
        url: pagelangcode.value !== 'en' ? `/${item.path}` : item.path,
      };
      parsedTemplates.push(parsed);
      setTemplates(parsedTemplates);
    });
  }, [results]);

  function handleButtonClick() {
    setLocalSearch(inputValue);
  }

  const searchContainerSpacing = {
    desktop: {
      top: 16,
      bottom: 32,
    },
    laptop: {
      top: 0,
      bottom: 0,
    },
    tablet: {
      top: 0,
      bottom: 0,
    },
    mobile: {
      top: 0,
      bottom: 0,
    },
  };

  const cardsContainerSpacing = {
    desktop: {
      top: 64,
      bottom: 0,
    },
    laptop: {
      top: 64,
      bottom: 0,
    },
    tablet: {
      top: 32,
      bottom: 0,
    },
    mobile: {
      top: 32,
      bottom: 0,
    },
  };

  const backSpacing = {
    desktop: {
      top: 8,
      bottom: 32,
    },
    laptop: {
      top: 8,
      bottom: 32,
    },
    tablet: {
      top: 8,
      bottom: 32,
    },
    mobile: {
      top: 8,
      bottom: 32,
    },
  };

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!inputValue) return;
    if (event.key === 'Enter') {
      handleButtonClick();
    }
  }

  useEffect(() => {
    function getCDPurl(lang: string) {
      let url = '/templates/';
      switch (lang) {
        case 'pt-br': {
          url = '/' + lang + '/modelos/';
          break;
        }
        case 'es': {
          url = '/' + lang + '/plantillas/';
          break;
        }
        case 'de': {
          url = '/' + lang + '/vorlagen/';
          break;
        }
        case 'fr': {
          url = '/' + lang + '/modeles/';
          break;
        }
        case 'it': {
          url = '/' + lang + '/templates/';
          break;
        }
        default: {
          url = '/templates/';
          break;
        }
      }
      return url;
    }
    const fullBreadcrumb = [
      {
        url: getCDPurl(pagelangcode.value),
        title: translateFromContent('templates.breadcrumbback'),
      },
    ].concat(localSearch ? [{ url: localSearch, title: localSearch }] : []);
    setBreadcrumb(fullBreadcrumb);
    let finalText = '';
    switch (pagelangcode.value) {
      case 'en': {
        finalText = localSearch.charAt(0).toUpperCase() + localSearch.slice(1) + ' templates';
        break;
      }
      case 'es': {
        finalText = 'Plantillas de ' + localSearch.charAt(0).toUpperCase() + localSearch.slice(1);
        break;
      }
      case 'fr': {
        finalText = 'Modèles ' + localSearch.charAt(0).toUpperCase() + localSearch.slice(1);
        break;
      }
      case 'de': {
        finalText = localSearch.charAt(0).toUpperCase() + localSearch.slice(1) + '-Vorlagen';
        break;
      }
      case 'it': {
        finalText = 'Modelli su ' + localSearch.charAt(0).toUpperCase() + localSearch.slice(1);
        break;
      }
      case 'pt-br': {
        finalText = 'Modelos de ' + localSearch.charAt(0).toUpperCase() + localSearch.slice(1);
        break;
      }
      default: {
        finalText = 'Results for:';
        break;
      }
    }

    setDisplayUpperResultsText(finalText);
  }, [localSearch]);

  const backgroundCards: BackgroundDTO = {
    backgroundType: 'solid',
    baseColor: { color: 'rWhite' },
  };

  return (
    <Section background={parseBackground(behaviorSettings.rowStyle.background)}>
      <ComponentContainer
        customSpacing={searchContainerSpacing}
        style={{
          textAlign: 'center',
        }}
      >
        <ComponentContainer customSpacing={backSpacing}>
          <Breadcrumb items={fullBreadcrumb} />
        </ComponentContainer>
        {localSearch && (
          <Text
            variant="title2"
            style={{
              paddingBottom: '32px',
            }}
          >
            {upperResultsText}
          </Text>
        )}
        <Flex
          flexDirection={'row'}
          alignItems="center"
          justifyContent="center"
          css={`
            @media ${theme.device.laptop} {
              max-width: 40%;
              margin: auto;
            }
          `}
        >
          <Input
            size={'medium'}
            variant="base"
            placeholder={placeholder}
            value={inputValue}
            onChange={e => setInputValue((e.target as HTMLInputElement).value)}
            onKeyDown={handleKeyPress}
          />
          <div style={{ paddingLeft: '16px' }}>
            <Button
              style={{ margin: '16px 0', display: 'flex' }}
              variant={buttonStyle && buttonStyle?.variant ? buttonStyle?.variant : 'primary'}
              onClick={handleButtonClick}
            >
              <Icon name={'Search'}></Icon>
            </Button>
          </div>
        </Flex>
        {localSearch && (
          <Text
            variant="paragraphS"
            $color={'Onyx'}
            style={{
              paddingBottom: '24px',
            }}
          >
            {results.length} {resultText}
          </Text>
        )}
      </ComponentContainer>
      <Flex>
        <Cards
          customSpacing={cardsContainerSpacing}
          background={parseBackground(backgroundCards)}
          cards={templates}
          variant="grid"
          elementsPerRow={elementsPerRow}
          masonry={true}
          templateCard={true}
        />
      </Flex>
    </Section>
  );
};
